import { createContext, useCallback, useContext, useState } from 'react';

/**
 * Context for managing text selection search functionality.
 * Allows opening the TranscriptSearchModal from anywhere with a pre-filled search term.
 */
const TextSelectionSearchContext = createContext(null);

/**
 * Cleans text for search by:
 * - Trimming whitespace
 * - Removing special characters (commas, quotes, etc.)
 * - Collapsing multiple spaces
 */
const cleanTextForSearch = (text) => {
  if (!text) return '';

  return (
    text
      .trim()
      // Remove common special characters but keep alphanumeric and spaces
      .replace(/[.,;:!?'""\u201C\u201D\u2018\u2019()[\]{}/<>@#$%^&*+=|\\~`]/g, ' ')
      // Collapse multiple spaces into single space
      .replace(/\s+/g, ' ')
      .trim()
  );
};

export const TextSelectionSearchProvider = ({ children }) => {
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [initialSearchTerm, setInitialSearchTerm] = useState('');

  /**
   * Opens the search modal with the provided text as the initial search term.
   * Text is automatically cleaned (special characters removed).
   * If text is empty or null, opens the modal without a pre-filled search term.
   */
  const openSearchWithText = useCallback((text) => {
    const cleanedText = text ? cleanTextForSearch(text) : '';
    setInitialSearchTerm(cleanedText);
    setIsSearchModalOpen(true);
  }, []);

  /**
   * Opens the search modal without any pre-filled search term.
   */
  const openSearchModal = useCallback(() => {
    setInitialSearchTerm('');
    setIsSearchModalOpen(true);
  }, []);

  /**
   * Closes the search modal and clears the initial search term.
   */
  const closeSearchModal = useCallback(() => {
    setIsSearchModalOpen(false);
    // Keep the search term briefly for the modal close animation
    setTimeout(() => setInitialSearchTerm(''), 300);
  }, []);

  const value = {
    isSearchModalOpen,
    initialSearchTerm,
    openSearchWithText,
    openSearchModal,
    closeSearchModal,
    cleanTextForSearch,
  };

  return (
    <TextSelectionSearchContext.Provider value={value}>
      {children}
    </TextSelectionSearchContext.Provider>
  );
};

/**
 * Hook to access the text selection search context.
 * Use this to open the search modal with highlighted text.
 *
 * @example
 * const { openSearchWithText } = useTextSelectionSearch();
 * openSearchWithText("selected text here");
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useTextSelectionSearch = () => {
  const context = useContext(TextSelectionSearchContext);
  if (!context) {
    throw new Error('useTextSelectionSearch must be used within a TextSelectionSearchProvider');
  }
  return context;
};

export default TextSelectionSearchContext;
